<template>
    <div class="wb-accordion"
         :class="classes"
    >
        <div ref="targetRef"
             class="wb-accordion__head"
             @click="handleClickToggle"
        >
            <span class="wb-accordion__title">
                <slot name="title" />
            </span>
            <slot name="arrow"
                  v-bind="{ isOpenState }"
            >
                <WBIcon v-if="arrowType === 'arrow'"
                        name="arrow-down"
                        class="wb-accordion__icon"
                />
                <div v-else
                     class="wb-accordion__plus"
                     :class="{ _open: isOpenState }"
                />
            </slot>
        </div>
        <transition :css="false"
                    @before-enter="onBeforeEnter"
                    @enter="onEnter"
                    @after-enter="onAfterEnter"
                    @before-leave="onBeforeLeave"
                    @leave="onLeave"
        >
            <div v-if="isOpenState"
                 ref="bodyRef"
                 class="wb-accordion__body"
            >
                <div class="wb-accordion__content">
                    <slot name="content" />
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
    import { useAccordion } from '~/src/shared/composables/client/accordion/useAccordion'

    const props = withDefaults(defineProps<{
        isOpen?: boolean
        outsideControl?: boolean
        theme?: 'default' | 'white'
        arrowType?: 'arrow' | 'plus'
        id?: number
        padding?: 'default' | 'large'
    }>(), {
        isOpen: false,
        outsideControl: false,
        theme: 'default',
        arrowType: 'arrow',
    })
    const emit = defineEmits<{
        (e: 'toggle', index?: number): void
    }>()
    const {
        targetRef,
        isOpenRef,
        bodyRef,
        handleToggle,
        onBeforeEnter,
        onEnter,
        onAfterEnter,
        onBeforeLeave,
        onLeave,
    } = useAccordion()

    const classes = computed(() => ({
        _open: isOpenRef.value,
        [`_theme-${props.theme}`]: props.theme,
        [`_padding-${props.padding}`]: props.padding,
    }))

    const isOpenState = computed(() => props.outsideControl ? props.isOpen : isOpenRef.value)

    function handleClickToggle() {
        if (props.outsideControl) {
            emit('toggle', props.id)
        } else {
            handleToggle()
        }
    }
</script>

<style scoped lang="scss">
    .wb-accordion {
        $accordion: &;

        --accordion-padding: 20px;

        border-radius: 16px;
        background-color: var(--accordion-background);

        &__head {
            display: flex;
            align-items: center;
            padding: var(--accordion-padding);
            cursor: pointer;
            user-select: none;
        }

        &__title {
            margin-right: auto;
        }

        &__content {
            padding: 0 var(--accordion-padding) var(--accordion-padding);
        }

        &__icon {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-left: 16px;
            transition: transform 0.2s ease;
        }

        &._open {
            #{$accordion}__icon {
                transform: rotate(180deg);
            }
        }

        &._theme-default {
            --accordion-background: var(--blue-50);

            width: 100%;
        }

        &._theme-white {
            background-color: var(--white);
        }

        &._padding-default {
            --accordion-padding: 20px;
        }

        &._padding-large {
            --accordion-padding: 24px;

            border-radius: 30px;
        }

        &__plus {
            position: relative;
            width: 32px;
            height: 32px;
            margin-left: 20px;

            &:before,
            &:after {
                content: "";
                position: absolute;
                top: 50%;
                left: 50%;
                width: 16px;
                height: 2px;
                border-radius: 2px;
                background-color: var(--grey-500);
            }

            &:before {
                transform: translate(-50%, -50%) rotate(90deg);
                transition: transform 0.2s ease-in-out;
            }

            &:after {
                transform: translate(-50%, -50%);
            }

            &._open {
                &:before {
                    transform: translate(-50%, -50%) rotate(180deg);
                }
            }
        }
    }
</style>
